/**
 * ReviewsPage ✦ iter228 — /reviews
 *
 * Premium trust hub. Heritage styling (cream + champagne gold).
 * Sections:
 *   1. Hero — heading + subheading + editable stats row.
 *   2. Featured Reviews — 3-6 large premium cards.
 *   3. All Reviews — filterable grid (product/event/tag chips).
 *   4. Memories Shared — masonry gallery of customer photos with lightbox.
 *   5. CTA strip — Heritage primary buttons.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ServicesNav from '../components/services/ServicesNav';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';

const HERITAGE = {
  bg: '#F8F5EF',
  primary: '#4B371E',
  secondary: '#7A5C3E',
  accent: '#9C7C2E',
  accentSoft: '#C9B07A',
  text: '#2E2E2E',
  textDim: '#6B5E4F',
  cardBg: '#FFFFFF',
  cardShadow: '0 18px 60px rgba(75,55,30,0.12)',
};

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

const FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'product:blossom-booth', label: 'Blossom Booth' },
  { key: 'product:woodland-mirror-booth', label: 'Mirror Booth' },
  { key: 'product:woodland-audio-guestbook', label: 'Audio Guestbook' },
  { key: 'product:woodland-video-guestbook', label: 'Video Guestbook' },
  { key: 'product:woodland-memory-post-box', label: 'Memory Post Box' },
  { key: 'event_type:wedding', label: 'Weddings' },
  { key: 'event_type:corporate', label: 'Corporate' },
  { key: 'event_type:birthday', label: 'Birthday' },
  { key: 'event_type:prom', label: 'Prom' },
  { key: 'event_type:christmas', label: 'Christmas' },
  { key: 'event_type:other', label: 'Other' },
];

const Stars = ({ count, size = 14 }) => (
  <span aria-label={`${count} of 5 stars`} style={{ color: HERITAGE.accent, fontSize: size, letterSpacing: 2 }}>
    {'★'.repeat(Math.max(0, Math.min(5, count)))}
    <span style={{ color: HERITAGE.accentSoft }}>{'★'.repeat(5 - Math.max(0, Math.min(5, count)))}</span>
  </span>
);

const SourceBadge = ({ source }) => {
  const map = {
    facebook: 'Facebook', add_to_event: 'Add To Event',
    google: 'Google', email: 'Email', website: 'Website', other: 'Other',
  };
  return (
    <span data-testid={`review-source-${source}`} style={{
      fontFamily: SERIF, fontSize: 10.5, letterSpacing: '0.22em',
      textTransform: 'uppercase', color: HERITAGE.accent,
      background: 'rgba(156,124,46,0.10)',
      padding: '4px 9px', borderRadius: 999,
    }}>{map[source] || 'Website'}</span>
  );
};

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    document.title = 'Reviews ✦ EventDrop — Loved by Couples Across North Wales & Beyond';
    let cancelled = false;
    (async () => {
      try {
        const [r, s] = await Promise.all([
          axios.get(`${API}/reviews?limit=300`),
          axios.get(`${API}/reviews/stats`),
        ]);
        if (cancelled) return;
        setReviews(r.data.reviews || []);
        setStats(s.data || null);
      } catch (e) { /* silent — empty state below */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return reviews;
    const [k, v] = filter.split(':');
    return reviews.filter(r => (r[k] || '').toLowerCase() === v.toLowerCase());
  }, [reviews, filter]);

  const featured = useMemo(() => reviews.filter(r => r.is_featured).slice(0, 6), [reviews]);
  const galleryImages = useMemo(() => {
    const out = [];
    for (const r of reviews) {
      for (const u of (r.image_urls || [])) {
        out.push({ url: mediaUrl(u), name: r.customer_name });
      }
    }
    return out;
  }, [reviews]);

  return (
    <div data-testid="reviews-page" style={{
      minHeight: '100vh', background: HERITAGE.bg, color: HERITAGE.text, fontFamily: SERIF,
    }}>
      <ServicesNav active="reviews" />
      <Hero stats={stats} />
      {featured.length > 0 && <FeaturedReviews items={featured} />}
      <AllReviews reviews={filtered} filter={filter} setFilter={setFilter} />
      {galleryImages.length > 0 && (
        <MemoriesShared images={galleryImages} onPick={setLightboxSrc} />
      )}
      <CTAStrip />
      <ReviewsFooter />
      {lightboxSrc && (
        <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
};

// ─── Hero ───────────────────────────────────────────────────────────
const Hero = ({ stats }) => {
  const o = stats?.overrides || {};
  const avg = stats?.average_rating || 0;
  const total = stats?.total_reviews || 0;
  return (
    <section data-testid="reviews-hero" style={{
      padding: 'clamp(70px, 10vw, 130px) clamp(20px, 5vw, 60px) clamp(40px, 6vw, 80px)',
      borderBottom: `1px solid rgba(122,92,62,0.14)`,
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: HERITAGE.secondary, marginBottom: 16,
        }}>✦ Real reviews from real customers</div>
        <h1 data-testid="reviews-hero-heading" style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(34px, 5.5vw, 60px)', letterSpacing: '-0.025em',
          color: HERITAGE.primary, margin: '0 0 22px', lineHeight: 1.05,
        }}>Loved by Couples Across North Wales &amp; Beyond</h1>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.6vw, 18px)',
          color: HERITAGE.textDim, maxWidth: 740, margin: '0 auto 14px', lineHeight: 1.7,
        }}>
          Real weddings. Real events. Real memories. Discover why EventDrop continues to receive
          outstanding feedback from couples, families and businesses.
        </p>
        <div style={{ marginTop: 50 }}>
          <div style={{
            display: 'grid', gap: 24,
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            maxWidth: 980, margin: '0 auto',
          }}>
            <StatTile icon="★" big={avg ? avg.toFixed(1) : '5.0'} label={`${total} Reviews`} testid="hero-stat-rating" />
            <StatTile icon="❤" big={o.happy_clients || '50+'} label="Happy Clients" testid="hero-stat-happy" />
            <StatTile icon="📸" big={o.photos_captured || 'Thousands'} label="Photos Captured" testid="hero-stat-photos" />
            <StatTile icon="🎉" big={o.events_covered || 'Weddings ✦ Parties ✦ Corporate'}
              label={o.years_trading ? `${o.years_trading} Years Trading` : 'Events Across the UK'}
              small testid="hero-stat-events" />
          </div>
        </div>
      </div>
    </section>
  );
};

const StatTile = ({ icon, big, label, small = false, testid }) => (
  <div data-testid={testid} style={{
    background: HERITAGE.cardBg,
    border: `1px solid rgba(156,124,46,0.18)`,
    borderRadius: 14, padding: '26px 18px',
    boxShadow: HERITAGE.cardShadow, textAlign: 'center',
  }}>
    <div style={{ fontSize: 22, marginBottom: 8, color: HERITAGE.accent }}>{icon}</div>
    <div style={{
      fontFamily: SERIF, fontWeight: 500,
      fontSize: small ? 'clamp(15px, 1.8vw, 19px)' : 'clamp(24px, 3vw, 34px)',
      color: HERITAGE.primary, letterSpacing: '-0.01em',
      lineHeight: 1.15, marginBottom: 6,
    }}>{big}</div>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
      color: HERITAGE.textDim, letterSpacing: '0.04em',
    }}>{label}</div>
  </div>
);

// ─── Featured Reviews ───────────────────────────────────────────────
const FeaturedReviews = ({ items }) => (
  <section data-testid="reviews-featured" style={{
    padding: 'clamp(60px, 8vw, 90px) clamp(20px, 5vw, 60px)',
    background: '#FFFCF6',
  }}>
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <SectionEyebrow>✦ Featured Reviews</SectionEyebrow>
      <SectionHeading>Hand-picked stories from recent events</SectionHeading>
      <div style={{
        display: 'grid', gap: 22, marginTop: 40,
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      }}>
        {items.map(r => <BigReviewCard key={r.review_id} review={r} />)}
      </div>
    </div>
  </section>
);

const BigReviewCard = ({ review }) => {
  const photo = (review.image_urls || [])[0];
  return (
    <article data-testid={`review-card-${review.review_id}`} style={{
      background: HERITAGE.cardBg,
      border: `1px solid rgba(156,124,46,0.22)`,
      borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 22px 60px rgba(75,55,30,0.14)',
      display: 'flex', flexDirection: 'column',
      position: 'relative',
    }}>
      {review.is_featured && (
        <span style={{
          position: 'absolute', top: 14, right: 14, zIndex: 2,
          background: HERITAGE.accent, color: '#FFF8E7',
          fontFamily: SERIF, fontSize: 10, letterSpacing: '0.22em',
          textTransform: 'uppercase', padding: '5px 11px', borderRadius: 999,
        }}>★ Featured</span>
      )}
      {photo && (
        <div style={{
          backgroundImage: `url(${mediaUrl(photo)})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          aspectRatio: '5 / 3',
        }} />
      )}
      <div style={{ padding: '24px 24px 26px' }}>
        <Stars count={review.rating} size={16} />
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 15, color: HERITAGE.text,
          lineHeight: 1.65, margin: '14px 0 18px',
        }}>"{review.review_text}"</p>
        <div style={{
          fontFamily: SERIF, fontWeight: 500, fontSize: 15,
          color: HERITAGE.primary, letterSpacing: '-0.005em',
        }}>{review.customer_name}</div>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12.5, color: HERITAGE.textDim, marginTop: 4,
        }}>
          {[review.event_type, review.venue, review.event_date].filter(Boolean).join(' · ')}
        </div>
        <div style={{ marginTop: 12 }}>
          <SourceBadge source={review.review_source} />
        </div>
      </div>
    </article>
  );
};

// ─── All Reviews — filterable ───────────────────────────────────────
const AllReviews = ({ reviews, filter, setFilter }) => (
  <section data-testid="reviews-all" style={{
    padding: 'clamp(60px, 8vw, 100px) clamp(20px, 5vw, 60px)',
  }}>
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <SectionEyebrow>✦ Every review</SectionEyebrow>
      <SectionHeading>Browse by product or event type</SectionHeading>
      <div data-testid="review-filter-chips" style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
        justifyContent: 'center', margin: '28px 0 36px',
      }}>
        {FILTER_CHIPS.map(c => {
          const active = filter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`review-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              style={{
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.04em',
                padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${active ? HERITAGE.accent : 'rgba(122,92,62,0.22)'}`,
                background: active ? HERITAGE.accent : 'rgba(122,92,62,0.04)',
                color: active ? '#FFF8E7' : HERITAGE.primary,
                transition: 'all 160ms ease',
              }}
            >{c.label}</button>
          );
        })}
      </div>
      {reviews.length === 0 ? (
        <p style={{
          textAlign: 'center', fontFamily: SERIF, fontStyle: 'italic',
          color: HERITAGE.textDim, padding: '40px 0',
        }}>No reviews yet for this filter — check back soon.</p>
      ) : (
        <div data-testid="review-grid" style={{
          display: 'grid', gap: 18,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {reviews.map(r => <SmallReviewCard key={r.review_id} review={r} />)}
        </div>
      )}
    </div>
  </section>
);

const SmallReviewCard = ({ review }) => {
  const photo = (review.image_urls || [])[0];
  return (
    <article data-testid={`review-card-${review.review_id}`} style={{
      background: HERITAGE.cardBg,
      border: `1px solid rgba(122,92,62,0.14)`,
      borderRadius: 12, padding: '20px 20px 22px',
      boxShadow: HERITAGE.cardShadow,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <Stars count={review.rating} />
        <SourceBadge source={review.review_source} />
      </div>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13.5, color: HERITAGE.text,
        lineHeight: 1.6, margin: '0 0 14px', flex: 1,
      }}>"{review.review_text}"</p>
      {photo && (
        <div style={{
          backgroundImage: `url(${mediaUrl(photo)})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          aspectRatio: '4 / 3', borderRadius: 8, marginBottom: 14,
        }} />
      )}
      <div style={{
        fontFamily: SERIF, fontWeight: 500, fontSize: 14,
        color: HERITAGE.primary,
      }}>{review.customer_name}</div>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11.5, color: HERITAGE.textDim, marginTop: 3,
      }}>
        {[review.event_type, review.venue].filter(Boolean).join(' · ')}
      </div>
    </article>
  );
};

// ─── Memories Shared (masonry-ish) ──────────────────────────────────
const MemoriesShared = ({ images, onPick }) => (
  <section data-testid="reviews-memories" style={{
    padding: 'clamp(60px, 8vw, 100px) clamp(20px, 5vw, 60px)',
    background: '#FFFCF6', borderTop: `1px solid rgba(122,92,62,0.10)`,
  }}>
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <SectionEyebrow>✦ Memories Shared</SectionEyebrow>
      <SectionHeading>Real moments captured by our customers</SectionHeading>
      <div style={{
        columnCount: 4, columnGap: 14, marginTop: 36,
      }} className="memories-masonry">
        {images.map((img, i) => (
          <button key={i} type="button" onClick={() => onPick(img.url)}
            data-testid={`memory-tile-${i}`}
            style={{
              all: 'unset', cursor: 'pointer', display: 'block',
              marginBottom: 14, width: '100%', breakInside: 'avoid',
              borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 12px 30px rgba(75,55,30,0.12)',
              transition: 'transform 180ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <img src={img.url} alt={`${img.name} memory`} loading="lazy"
              style={{ width: '100%', height: 'auto', display: 'block' }} />
          </button>
        ))}
      </div>
    </div>
    <style>{`
      @media (max-width: 1080px) { .memories-masonry { column-count: 3 !important; } }
      @media (max-width: 760px)  { .memories-masonry { column-count: 2 !important; } }
      @media (max-width: 480px)  { .memories-masonry { column-count: 1 !important; } }
    `}</style>
  </section>
);

const Lightbox = ({ src, onClose }) => (
  <div onClick={onClose} data-testid="memory-lightbox" style={{
    position: 'fixed', inset: 0, background: 'rgba(15,11,9,0.92)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20, cursor: 'pointer',
  }}>
    <img src={src} alt="" style={{
      maxWidth: '92vw', maxHeight: '92vh', boxShadow: '0 40px 120px rgba(0,0,0,0.5)',
      borderRadius: 10,
    }} />
  </div>
);

// ─── CTA + Footer + helpers ─────────────────────────────────────────
const CTAStrip = () => (
  <section data-testid="reviews-cta" style={{
    padding: 'clamp(60px, 8vw, 100px) clamp(20px, 5vw, 60px)',
    textAlign: 'center',
  }}>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <SectionEyebrow>✦ Your turn</SectionEyebrow>
      <SectionHeading>Ready to Create Your Own Memories?</SectionHeading>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
        <Link to="/photobooth-hire" data-testid="reviews-cta-photobooths" style={{
          textDecoration: 'none', background: HERITAGE.accent, color: '#FFF8E7',
          borderRadius: 999, padding: '14px 28px', fontFamily: SERIF,
          fontSize: 13.5, letterSpacing: '0.14em', textTransform: 'uppercase',
          boxShadow: '0 12px 30px rgba(156,124,46,0.32)',
        }}>View Photobooths</Link>
        <Link to="/photobooth-hire#availability" data-testid="reviews-cta-availability" style={{
          textDecoration: 'none', background: 'transparent', color: HERITAGE.primary,
          border: `1px solid ${HERITAGE.secondary}`,
          borderRadius: 999, padding: '14px 28px', fontFamily: SERIF,
          fontSize: 13.5, letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>Check Availability</Link>
      </div>
    </div>
  </section>
);

const ReviewsFooter = () => (
  <footer style={{
    padding: '36px clamp(20px, 5vw, 60px) 44px',
    borderTop: `1px solid rgba(122,92,62,0.14)`,
    fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
    color: HERITAGE.textDim, textAlign: 'center',
  }}>
    <Link to="/leave-a-review" data-testid="reviews-leave-review-link" style={{
      color: HERITAGE.accent, marginRight: 18, textDecoration: 'underline',
      textUnderlineOffset: 4,
    }}>Leave us a review →</Link>
    EventDrop · hello@eventdrop.co · eventdrop.co
  </footer>
);

const SectionEyebrow = ({ children }) => (
  <div style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11.5, letterSpacing: '0.32em', textTransform: 'uppercase',
    color: HERITAGE.secondary, textAlign: 'center', marginBottom: 14,
  }}>{children}</div>
);

const SectionHeading = ({ children }) => (
  <h2 style={{
    fontFamily: SERIF, fontWeight: 500, textAlign: 'center',
    fontSize: 'clamp(28px, 4vw, 44px)', letterSpacing: '-0.02em',
    color: HERITAGE.primary, margin: 0, lineHeight: 1.1,
  }}>{children}</h2>
);

export default ReviewsPage;
